'use client'

import * as React from 'react'
import { RefObject } from 'react'
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'

import { cn } from '../../lib/cn'
import { CloseIcon } from '../../icons/dialog.tsx'
import { useTranslation } from 'react-i18next'

function Dialog ({ ...props }: DialogPrimitive.Root.Props): JSX.Element {
  return <DialogPrimitive.Root data-slot='dialog' {...props} />
}

function DialogTrigger ({ ...props }: DialogPrimitive.Trigger.Props): JSX.Element {
  return <DialogPrimitive.Trigger data-slot='dialog-trigger' {...props} />
}

function DialogPortal ({ ...props }: DialogPrimitive.Portal.Props): JSX.Element {
  return <DialogPrimitive.Portal data-slot='dialog-portal' {...props} />
}

function DialogClose ({ ...props }: DialogPrimitive.Close.Props): JSX.Element {
  return <DialogPrimitive.Close data-slot='dialog-close' {...props} />
}

function DialogOverlay ({
  className,
  ...props
}: DialogPrimitive.Backdrop.Props): JSX.Element {
  return (
    <DialogPrimitive.Backdrop
      data-slot='dialog-overlay'
      className={cn('data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 bg-black/50 duration-100 supports-backdrop-filter:backdrop-blur-xs fixed inset-0 isolate z-50', className)}
      {...props}
    />
  )
}

function DialogContent ({
  className,
  container,
  children,
  showCloseButton = true,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean
  container?: HTMLElement | ShadowRoot | null | RefObject<HTMLElement | ShadowRoot | null>
}): JSX.Element {
  const { t } = useTranslation('widget')
  return (
    <DialogPortal container={container}>
      <DialogOverlay />
      <DialogPrimitive.Popup
        data-slot='dialog-content'
        className={cn(
          'bg-white data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 ring-foreground/10 grid rounded-xl text-sm duration-100 fixed top-1/2 left-1/2 z-50 w-full -translate-x-1/2 -translate-y-1/2 outline-none',
          '[--dialog-margin:2rem] max-w-[min(100%-var(--dialog-margin,2rem),var(--smt-site-max-width-xl,100%)-(var(--dialog-margin,var(--smt-spacing-md,1rem)*2)))]',
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot='dialog-close'
            render={
              <button
                className='absolute top-6 right-8 text-brand-primary font-bold flex items-center gap-2 cursor-pointer'
              >
                <span className=''>{t('close', 'Close')}</span>
                <CloseIcon />
              </button>
            }
          />
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  )
}

function DialogHeader ({ className, ...props }: React.ComponentProps<'div'>): JSX.Element {
  return (
    <div
      data-slot='dialog-header'
      className={cn('rounded-t-xl p-6 @desktop:px-8 gap-2 flex flex-col bg-brand-primary-lightest border-b border-b-brand-primary-light', className)}
      {...props}
    />
  )
}

function DialogBody ({ className, children }: React.ComponentProps<'div'>): JSX.Element {
  return (
    <div
      data-slot='dialog-body'
      className={cn('p-6 @desktop:p-12', className)}
    >{children}
    </div>
  )
}

function DialogFooter ({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean
}): JSX.Element {
  const { t } = useTranslation('widget')
  return (
    <div
      data-slot='dialog-footer'
      className={cn(
        'bg-muted/50 -mx-4 -mb-4 rounded-b-xl border-t p-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<button>{t('close', 'Close')}</button>} />
      )}
    </div>
  )
}

function DialogTitle ({ className, ...props }: DialogPrimitive.Title.Props): JSX.Element {
  return (
    <DialogPrimitive.Title
      data-slot='dialog-title'
      className={cn('text-2xl leading-none font-bold text-brand-primary', className)}
      {...props}
    />
  )
}

function DialogDescription ({
  className,
  ...props
}: DialogPrimitive.Description.Props): JSX.Element {
  return (
    <DialogPrimitive.Description
      data-slot='dialog-description'
      className={cn('text-muted-foreground *:[a]:hover:text-foreground text-sm *:[a]:underline *:[a]:underline-offset-3', className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogBody,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger
}
